import { useRef, useState } from "react";

import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "../../../hooks/use-toast";
import { post } from "../../utils/request";
import { useAuthStore } from "../../../stores/authStore";
export function OtpForm({ length = 6 }) {
  const { auth } = useAuthStore();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(Array(length).fill(""));
  const inputs = useRef([]);

  const handleChange = (e, index) => {
    const value = e.target.value.replace(/[^0-9]/g, ""); // Allow only numbers
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < length - 1) {
      inputs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text/plain")
      .replace(/[^0-9]/g, ""); // Limpia el texto pegado

    if (pastedData.length === length) {
      const newOtp = [...otp];
      for (let i = 0; i < length; i++) {
        newOtp[i] = pastedData[i] || "";
      }
      setOtp(newOtp);

      // Enfocar el último input después de pegar
      inputs.current[length - 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputs.current[index - 1].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const email = searchParams.get("email");

    if (!email) {
      toast({
        variant: "error",
        title: "Correo electrónico",
        description: "Correo electrónico inválido",
      });
      navigate("/sign-in");
      return;
    }

    if (otp.join("").length !== 6) {
      toast({
        variant: "error",
        title: "Codigo OTP",
        description: "Ingrese el código de 6 dígitos",
      });
      return;
    }

    setLoading(true);

    try {
      const token = await post({
        path: `/auth/validate-code`,
        data: {
          email,
          code: otp.join(""),
        },
      });

      auth.setAccessToken(token);
      toast({
        variant: "success",
        title: "Código OTP",
        description: "Código OTP verificado correctamente",
      });
      navigate("/");
      setLoading(false);
    } catch (error) {
      toast({
        variant: "error",
        title: "Código OTP",
        description: "Código OTP inválido. Inténtelo de nuevo.",
      });
      setLoading(false);
    }
  };

  const handleResendForm = () => {
    setOtp(Array(length).fill(""));
  };
  const resendCode = async () => {
    const email = searchParams.get("email");
    if (!email) {
      toast({
        variant: "error",
        title: "Correo electrónico",
        description: "Correo electrónico inválido",
      });
      navigate("/sign-in");
      return;
    }

    setLoading(true);

    try {
      await post({
        path: `/auth/sing-in`,
        data: {
          email,
        },
      });
      setOtp(Array(length).fill(""));
      toast({
        variant: "success",
        title: "Código OTP",
        description: "Se ha enviado un nuevo código",
      });
      setLoading(false);
    } catch (error) {
      toast({
        variant: "error",
        title: "Código OTP",
        description: "Código OTP inválido. Inténtelo de nuevo.",
      });
      setLoading(false);
    }
  };

  return (
    <Stack 
      maxWidth={{ xs: "300px", sm: "380px" }} 
      paddingX={2}
      sx={{
        border: "1px solid #e0e0e0",  
        borderRadius: "15px", 
        padding: "26px", 
        backgroundColor: "white",
        boxShadow: 1,
      }}
    >
      <Typography
        fontWeight="700"
        color="black"
        mb={2}
        textAlign={"center"}
        sx={{ 
          fontSize: { xs: "1.29rem", sm: "1.5rem" }, 
          lineHeight: "2rem", 
          letterSpacing: "-.025em" }}
      >
        Validar código de autenticación
      </Typography>
      <Typography 
        fontWeight={"500"}
        color="black"
        mb={2}
        textAlign={"center"}
        sx={{fontSize: "1.06rem", lineHeight: "1.25rem", color: "#596173"}}
        >
        Ingrese el código de verificación de 6 dígitos enviado a su correo
        electrónico
      </Typography>
      <Stack spacing={2} marginTop={1}>
        <Box display="flex" gap={2} justifyContent="center">
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(ref) => (inputs.current[index] = ref)}
              value={digit}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste} // Permite pegar código
              inputProps={{
                maxLength: 1,
                style: { textAlign: "center", fontSize: "1.2rem" },
              }}
              variant="outlined"
              sx={{ 
                width: "40px",
                "& .MuiInputBase-input": {
                padding: "6px"},
                "& .MuiOutlinedInput-root": {
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#a95052", 
                },
              },
               }}
            />
          ))}
        </Box>
        <Stack direction="row" spacing={2} justifyContent="center">
          {" "}
          <Button
            type="button"
            disabled={loading}
            variant="contained"
            onClick={handleSubmit}
            sx={{
              backgroundColor: "#a95052",
              "&:hover": { backgroundColor: "#8c3e47" },
            }}
          >
            Verificar
          </Button>
          <Button
            variant="outlined"
            sx={{
              borderColor: "#a95052", 
              color: "#a95052", 
              "&:hover": {
                backgroundColor: "#a95052", 
                borderColor: "#a95052",
                color: "white",
              },
            }}
            onClick={handleResendForm}
            startIcon={<RefreshIcon />}
          >
            Reiniciar
          </Button>
        </Stack>
        <Typography 
          variant="body2" 
          align="center" 
          sx={{fontSize: "1.06rem", lineHeight: "1.25rem", color: "#596173"}}>
          ¿No recibió el código?{" "}
          <Button
            color="primary"
            sx={{
              p: 0,
              minWidth: "auto",
              color: "#596173",
              textDecoration: "underline",
              "&:hover": { 
                backgroundColor: "transparent", 
                color: "#8c3e47", 
                textDecoration: "underline",
              },
            }}
            onClick={resendCode}
          >
            Reenviar
          </Button>
        </Typography>
      </Stack>
    </Stack>
  );
}
